import { useIdentity, useLogger } from '@vp/ubik-context'
import { useState, useEffect } from 'react'

interface Vote {
  Id: string
  upvote: boolean
  feedbackReason: string | null
  feedbackDescription: string
  articleVersionId: string
}

export const useArticleVote = (articleId: string, locale: string) => {
  const identity = useIdentity()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [data, setData] = useState<Vote | null>(null)
  const logger = useLogger()

  const fetchVote = async (id?: string): Promise<Vote | null> => {
    const voteId = id || articleId

    if (!voteId) {
      logger.error('Missing article ID', { articleId, locale })
      setError(new Error('Article ID is required'))
      return null
    }

    try {
      setIsLoading(true)
      setError(null)

      const authHeader = identity.auth?.getAuthorizationHeader()

      const response = await fetch(`HELP_CENTER_SERVICE/feedback/${locale}/articles/${voteId}/latest`, {
        method: 'GET',
        headers: {
          Authorization: authHeader || '',
        },
      })

      if (!response.ok) {
        throw new Error(`Failed to fetch vote: ${response.status} ${response.statusText}`)
      }

      const result = await response.json()
      if (result?.code !== 200) {
        return null
      }

      setData(result)
      return result
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      logger.error('Failed to fetch vote', { articleId, locale, error, voteId })
      setError(error)
      return null
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!identity?.auth?.getAuthorizationHeader()) return
    fetchVote(articleId)
  }, [articleId, identity])

  return {
    fetchVote,
    isLoading,
    error,
    data,
  }
}
